import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { TruckerConfirmationService } from '../services/trucker-confirmation.service';
import { TextMessage } from '../models/TextMessage';

@Component({
    selector: 'texts-status',
    standalone: false,
    templateUrl: 'texts-status.component.html',
    styles: [`
      .message-container pre.collapsed {
        max-height: 3.0em;
        line-height: 1.5em;
        overflow: hidden;
        position: relative;
        text-overflow: ellipsis;
        white-space: pre-wrap;
        word-wrap: break-word;
      }

      .scrollable thead {
            position: sticky;
            top: 0;
            z-index: 10;
            background-color: white;
       }

      .scrollable thead th {
            background-color: white;
      }
 
      .expand-btn {
        cursor: pointer;
        font-size: 1.2rem;
      }

      tbody tr {
        height: 50px;
      }      
    `]
})
export class TextsStatusComponent implements OnChanges {
    constructor(private _truckerConfirmService: TruckerConfirmationService) {
    }

    @Input() dispatchId: number = 0;
    @Input() refresh: boolean = false;
    @Input() textsStatusModalId: string = 'textsStatusModal';
    @Input() parentComponent: string = 'schedules';
    @ViewChild('closeButton') closeButton;

    textList: TextMessage[] = [];
    filteredTextList: TextMessage[] = [];
    errorMessage: string = '';
    expandedTextRows = new Set<number>();
    expandedAckRows = new Set<number>();
    searchBySender: string = '';
    searchByDateTime: string = '';
    searchByRecipient: string = '';
    searchByInitiatingProcess: string = '';
    searchByTextDirection: string = '';
    searchByTextContent: string = '';
    searchByDeliveryStatus: string = '';
    searchByAckDetails: string = '';

    getTexts(): void {
        if (this.dispatchId > 0) {
            this._truckerConfirmService.getTextsStatus(this.dispatchId).subscribe({
                next: data => {
                    this.textList = data;
                    this.filteredTextList = data;
                },
                error: err => this.errorMessage = err
            });
        }
    }

    filterTextList(): void {
        this.filteredTextList = this.textList.filter(text => {
            const sender = this.searchBySender?.trim().toLowerCase() || '';
            const dateTime = this.searchByDateTime?.trim().toLowerCase() || '';
            const recipient = this.searchByRecipient?.trim().toLowerCase() || '';
            const process = this.searchByInitiatingProcess?.trim().toLowerCase() || '';
            const direction = this.searchByTextDirection?.trim().toLowerCase() || '';
            const content = this.searchByTextContent?.trim().toLowerCase() || '';
            const status = this.searchByDeliveryStatus?.trim().toLowerCase() || '';
            const ackDetails = this.searchByAckDetails?.trim().toLowerCase() || '';

            if (sender && !text.createdBy?.toLowerCase().includes(sender)) {
                return false;
            }
            if (dateTime) {
                const dateStr = new Date(text.timestampUtc).toLocaleString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true
                });
                if (!dateStr.toLowerCase().includes(dateTime)) {
                    return false;
                }
            }
            if (recipient) {
                const recipientStr = `${text.contactName} (${text.toPhoneNumber})`;
                if (!recipientStr.toLowerCase().includes(recipient)) {
                    return false;
                }
            }
            if (process && !text.initiatingProcess?.toLowerCase().includes(process)) {
                return false;
            }
            if (direction && !text.direction?.toLowerCase().includes(direction)) {
                return false;
            }
            if (content && !text.messageText?.toLowerCase().includes(content)) {
                return false;
            }
            if (status && !text.deliveryStatus?.toLowerCase().includes(status)) {
                return false;
            }
            if (ackDetails && !text.deliveryStatusDetails?.toLowerCase().includes(ackDetails)) {
                return false;
            }
            return true;
        });
    }

    toggleTextRow(index: number): void {
        if (this.expandedTextRows.has(index)) {
            this.expandedTextRows.delete(index);
        } else {
            this.expandedTextRows.add(index);
        }
    }

    toggleAckRow(index: number): void {
        if (this.expandedAckRows.has(index)) {
            this.expandedAckRows.delete(index);
        } else {
            this.expandedAckRows.add(index);
        }
    }

    needsExpand(text: string): boolean {
        if (!text) return false;
        const lineBreaks = (text.match(/\n/g) || []).length;
        if (lineBreaks >= 2) return true;
        return text.length > 160;
    }

    isTextRowExpanded(index: number): boolean {
        return this.expandedTextRows.has(index);
    }

    isAckRowExpanded(index: number): boolean {
        return this.expandedAckRows.has(index);
    }

    resetComponent(): void {
        this.textList = [];
        this.filteredTextList = [];
        this.expandedTextRows.clear();
        this.expandedAckRows.clear();
        this.searchBySender = '';
        this.searchByDateTime = '';
        this.searchByRecipient = '';
        this.searchByInitiatingProcess = '';
        this.searchByTextDirection = '';
        this.searchByTextContent = '';
        this.searchByDeliveryStatus = '';
        this.searchByAckDetails = '';
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['dispatchId'] || changes['refresh']) {
            this.resetComponent();
            this.getTexts();
        }
    }
}